import { useCallback, useEffect, useMemo } from 'react';

import { BigNumber } from 'ethers';
import moment from 'moment';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import toast from 'react-hot-toast';

import { useCalculateLtv } from '@/hooks/loans/useCalculateLTV';
import { useCalculatePrincipal } from '@/hooks/loans/useCalculatePrincipal';
import { useLoanTokens } from '@/hooks/query/loans/useLoanTokens';
import { convertValueToWei, convertWeiToToken } from '@/utils/convertValueToWei';
import { getContractDecimals } from '@/utils/getContractDecimals';
import { useErc20Contract } from 'smart-contracts/contracts';
import { IDesiredTerms } from 'types/loan';

import DesiredTerms, { IDesiredTermsProps, TContentArr } from './DesiredTerms';
import DurationPicker from './DurationPicker';

interface IChangableDesiredTerms extends Omit<IDesiredTermsProps, 'content' | 'control' | 'handleSubmit'> {}

interface IRepaymentPayload {
  loanAmount: bigint | BigNumber;
  aprBasisPoints: number;
  loanDuration: number;
}

function calculateInterest(loanAmount: BigNumber, aprBasisPoints: number, loanDurationSeconds: number): BigNumber {
  const BPS = 10000;
  const SECONDS_IN_YEAR = 31536000;

  return loanAmount
    .mul(aprBasisPoints)
    .mul(loanDurationSeconds)
    .div(BPS * SECONDS_IN_YEAR);
}

function getPayoffAmount(loan: IRepaymentPayload): [BigNumber, BigNumber] {
  const interestAmount = calculateInterest(BigNumber.from(loan.loanAmount), loan.aprBasisPoints, loan.loanDuration);

  const repaymentAmount = BigNumber.from(loan.loanAmount).add(interestAmount);

  return [repaymentAmount, interestAmount];
}

type TChangableDesiredTermsProps = Omit<IChangableDesiredTerms, 'Profit' | 'Actions'> & {
  Profit?: (params: { interestRate: number; symbol: string | undefined }) => JSX.Element;
  Actions?: (
    parameters: Pick<UseFormReturn<IDesiredTerms>, 'handleSubmit' | 'control'> & {
      values: Pick<IDesiredTerms, 'apr' | 'term' | 'principal' | 'currency'>;
    }
  ) => JSX.Element;
  hideExp?: boolean;
  assetValue?: number | null;
  disableLtv?: boolean;
};

function ChangableDesiredTerms({ Profit, Actions, hideExp = false, assetValue, disableLtv, ...props }: TChangableDesiredTermsProps) {
  const { control, handleSubmit, watch, setValue } = useFormContext<IDesiredTerms>();

  const { data: loanTokens } = useLoanTokens();

  const principal = watch('principal');
  const apr = watch('apr');
  const term = watch('term');
  const ltv = watch('ltv');
  const interestRate = watch('interestRate');
  const repay = watch('repay');
  const currency = watch('currency');
  const generateERC20Contract = useErc20Contract();
  const calculateLtv = useCalculateLtv();
  const calculatePrincipal = useCalculatePrincipal();
  const { data } = useLoanTokens();

  const findToken = useCallback((currencyAddress: string) => data?.find(({ address }) => address === currencyAddress)?.symbol, [data]);

  const symbol = useMemo(() => findToken?.(currency), [currency, findToken]);

  useEffect(() => {
    if (assetValue) {
      if (ltv && symbol) {
        setValue('principal', Number(calculatePrincipal(ltv, assetValue, symbol).toFixed(6)));
      } else if (principal && symbol) {
        setValue('ltv', Number(calculateLtv(principal, assetValue, symbol)));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetValue]);

  const promDecimals = useMemo(() => {
    if (!currency) return null;
    const ERC20Contract = generateERC20Contract(currency);
    if (!ERC20Contract) return null;

    return getContractDecimals(ERC20Contract);
  }, [currency, generateERC20Contract]);

  useEffect(() => {
    (async () => {
      const decimals = await promDecimals;

      if (apr && principal && decimals) {
        const [repayment, interestPayoffRate] = getPayoffAmount({
          aprBasisPoints: apr * 100,
          loanAmount: convertValueToWei(principal, decimals),
          loanDuration: Math.round(moment.duration(term, 'd').asSeconds()),
        });

        setValue('repay', convertWeiToToken(repayment, decimals));
        setValue('interestRate', Number(convertWeiToToken(interestPayoffRate, decimals)));
      }
    })();
  }, [apr, principal, term, setValue, promDecimals]);

  const content = useMemo(() => {
    const unit = loanTokens?.find(({ address }) => address === currency)?.symbol;

    const fields: TContentArr = [
      {
        key: 'Loan',
        formKey: 'principal',
        max: 8,
        customOnChange: (newPrincipal, newSymbol) => {
          if (assetValue && symbol) {
            setValue('ltv', Number(calculateLtv(Number(newPrincipal || principal), assetValue, newSymbol ? findToken?.(newSymbol) || symbol : symbol)));
          }
        },
        containerClassName: 'gap-0',
      },
      {
        key: 'Repay',
        unit,
        formKey: 'repay',
        disable: true,
        max: 7,
        value: Number(Number(repay).toFixed(6)),
      },
      {
        key: 'APR',
        unit: '%',
        formKey: 'apr',
        max: 7,
      },

      {
        CustomField: () => <DurationPicker name='term' control={control} label='Duration' />,
      },
    ];

    if (assetValue && !disableLtv) {
      fields.splice(3, 0, {
        key: 'LTV',
        unit: '%',
        formKey: 'ltv',
        disable: disableLtv || false,
        max: 12,
        customOnChange: (newLtv) => {
          if (assetValue && symbol) {
            setValue('principal', Number(Number(calculatePrincipal(Number(newLtv), assetValue, symbol)).toFixed(3)));
          }
        },
      });
    }

    const expField = {
      CustomField: () => <DurationPicker name='exp' control={control} label='Exp' />,
    };

    return hideExp ? fields : [...fields, expField];
  }, [loanTokens, repay, assetValue, hideExp, currency, symbol, setValue, calculateLtv, principal, findToken, control, disableLtv, calculatePrincipal]);

  return (
    <DesiredTerms
      {...props}
      {...(Actions && {
        Actions: () =>
          Actions({
            handleSubmit: (onSuccess) =>
              handleSubmit((values) => {
                if ([values.apr, values.principal, values.exp, values.term].some((field) => !field)) {
                  toast.error('All loan fields must be filled out to continue.');
                  return null;
                }

                return onSuccess(values);
              }),
            values: {
              apr,
              term,
              principal,
              currency,
            },
            control,
          }),
      })}
      {...(Profit && { Profit: () => Profit({ interestRate, symbol }) })}
      content={content}
      control={control}
    />
  );
}
export default ChangableDesiredTerms;
